/**
 * Gift Options custom element
 *
 * Handles the "Add Gift wrap?" toggle in the cart drawer and cart page.
 * - Toggle ON  → auto-adds the first available gift product (gift wrap) to cart
 * - Switch radio → removes previous gift item and adds the selected one
 * - Toggle OFF → removes the currently selected gift product from cart
 * - After every cart mutation the relevant Shopify sections are re-rendered so
 *   totals, item count, and the gift-options component itself always reflect the
 *   live cart state.
 *
 * Data attributes (set by gift-options.liquid, re-set on every Liquid re-render):
 *   data-active            "true" | "false"  – is a gift item in the cart?
 *   data-current-variant   variant ID string  – the variant currently in cart
 *   data-gift-wrap-variant variant ID string  – first available wrap variant
 *   data-gift-box-variant  variant ID string  – first available box variant
 */

let cartPagePanelOpen   = false;
let cartDrawerPanelOpen = false; // tracks whether user explicitly opened the panel in the cart drawer

class GiftOptions extends HTMLElement {
  connectedCallback() {
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.isMobile = this.mediaQuery.matches;
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.toggleBtn   = this.querySelector('[data-gift-toggle]');
    this.panel        = this.querySelector('[data-gift-panel]');
    this.radios       = Array.from(this.querySelectorAll('[data-gift-radio]'));
    this.collapseBtn  = this.querySelector('[data-gift-collapse]');
    // Derive initial collapsed state from the actual DOM so it stays in sync
    // regardless of how Liquid rendered the panel (hidden in cart drawer, visible on cart page).
    this.panelCollapsed = this.panel?.classList.contains('gift-options__panel--hidden') ?? false;

    this.toggleBtn?.addEventListener('click', this.handleToggle.bind(this));
    this.radios.forEach((r) => r.addEventListener('change', this.handleRadioChange.bind(this)));
    this.collapseBtn?.addEventListener('click', this.handleCollapseToggle.bind(this));

    // In the cart drawer the Liquid always renders the panel hidden.
    // Restore open state only when the user had explicitly opened it before a re-render.
    if (!this.isCartPage && this.isActive && cartDrawerPanelOpen) {
      this.panel?.classList.remove('gift-options__panel--hidden');
      this.panelCollapsed = false;
      this.collapseBtn?.setAttribute('aria-expanded', 'true');
      this.collapseBtn?.classList.remove('gift-options__collapse-btn--up');
    }

    this.messageInput = this.querySelector('[data-gift-message]');
    if (this.messageInput) {
      const savedNote = this.dataset.currentNote || '';
      this.messageInput.value = savedNote;
      this.updateCounter(savedNote.length);
      if (savedNote) this.updateGiftItemMessages(savedNote);
      this.messageInput.addEventListener('input', this.handleMessageInput.bind(this));
    }

    if (this.isCartPage) {
      if (cartPagePanelOpen) this.classList.add('gift-options--cart-open');
      this.cartPageEditBtnHandler = (e) => {
        const btn = e.target.closest('[data-gift-options-edit]');
        if (btn) this.toggleCartPage();
      };
      document.addEventListener('click', this.cartPageEditBtnHandler);

      const confirmBtn = this.querySelector('[data-gift-confirm]');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => this.closeCartPage());
      }
    }

    this.drawerOpenUnsub = subscribe(PUB_SUB_EVENTS.cartDrawerOpen, () => {
      this.refreshNoteFromServer();
    });

    this.pageHideHandler = () => this.flushPendingNote();
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  disconnectedCallback() {
    if (typeof this.drawerOpenUnsub === 'function') {
      this.drawerOpenUnsub();
      this.drawerOpenUnsub = null;
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }

    if (this.cartPageEditBtnHandler) {
      document.removeEventListener('click', this.cartPageEditBtnHandler);
      this.cartPageEditBtnHandler = null;
    }

    // Flush any unsaved note on disconnect (covers section re-renders mid-type).
    // pendingSaveNote — debounce hasn't fired yet; inFlightNote — fetch in progress.
    const unsavedNote = this.pendingSaveNote ?? this.inFlightNote;
    if (unsavedNote !== undefined) {
      clearTimeout(this.messageSaveTimer);
      this.messageSaveTimer = null;
      this.pendingSaveNote  = undefined;
      this.inFlightNote     = undefined;
      this.saveCartNote(unsavedNote);
    }
  }

  get isActive()       { return this.dataset.active         === 'true'; }
  get currentVariant() { return this.dataset.currentVariant || ''; }
  get wrapVariant()    { return this.dataset.giftWrapVariant || ''; }
  get boxVariant()     { return this.dataset.giftBoxVariant  || ''; }
  get firstVariant()   { return this.wrapVariant || this.boxVariant; }
  get hasNoOptions()   { return !this.wrapVariant && !this.boxVariant; }
  get isLoading()      { return this.dataset.loading === 'true'; }
  get isCartPage()     { return this.dataset.context === 'cart-page'; }

  async handleToggle() {
    if (this.isLoading) return;

    if (this.isActive) {
      const msgInput = this.querySelector('[data-gift-message]');
      if (msgInput) {
        clearTimeout(this.messageSaveTimer);
        this.messageSaveTimer = null;
        this.pendingSaveNote  = undefined;
        this.inFlightNote     = undefined;
        msgInput.value = '';
        this.updateCounter(0);
        this.setMessageStatus('');
      }
      if (this.isCartPage) cartPagePanelOpen   = false;
      else                  cartDrawerPanelOpen = false;
      if (this.currentVariant) {
        await this.removeGiftItem(this.currentVariant, '');
      } else {
        await this.saveCartNote('');
        this.setUIActive(false);
      }
    } else {
      if (this.hasNoOptions) {
        this.setUIActive(true);
        return;
      }
      if (this.isCartPage) cartPagePanelOpen   = true;
      else                 cartDrawerPanelOpen = true;
      await this.addGiftItem(this.firstVariant);
    }
  }

  async handleRadioChange(event) {
    if (this.isLoading) return;

    const newVariant = event.target.value;
    if (!newVariant || newVariant === this.currentVariant) return;

    const oldVariant     = this.currentVariant;
    const currentMessage = this.messageInput?.value ?? '';

    this.setLoading(true);
    try {
      // Flush debounced note save so it's persisted before sections re-render.
      if (this.pendingSaveNote !== undefined) {
        clearTimeout(this.messageSaveTimer);
        this.messageSaveTimer = null;
        const note = this.pendingSaveNote;
        this.pendingSaveNote = undefined;
        await this.saveCartNote(note);
      }

      if (oldVariant) {
        await this.cartUpdate({ updates: { [oldVariant]: 0 } });
      }
      await this.addGiftItem(newVariant, /* skipLoading */ true);

      // Restore message to any re-rendered instances whose textarea is blank.
      if (currentMessage) {
        document.querySelectorAll('gift-options').forEach((el) => {
          const input = el.querySelector('[data-gift-message]');
          if (input && !input.value) {
            input.value = currentMessage;
            el.updateCounter(currentMessage.length);
            el.updateGiftItemMessages(currentMessage);
          }
        });
      }
    } catch (err) {
      console.error('[GiftOptions] radio change error:', err);
      const prevRadio = this.querySelector(`[data-gift-radio][value="${oldVariant}"]`);
      const newRadio  = this.querySelector(`[data-gift-radio][value="${newVariant}"]`);
      if (prevRadio) prevRadio.checked = true;
      if (newRadio)  newRadio.checked  = false;
      this.updateOptionHighlight(oldVariant);
    } finally {
      this.setLoading(false);
    }
  }

  async addGiftItem(variantId, skipLoading = false) {
    if (!variantId) return;
    if (!skipLoading) this.setLoading(true);

    try {
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id:           parseInt(variantId, 10),
          quantity:     1,
          properties:   { _is_gift_option: 'true' },
          sections:     this.sectionsToRender(),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || `HTTP ${response.status}`);
      }

      const state = await response.json();
      this.cart?.renderContents(state);
      this.renderCartPageSections(state.sections);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-options', cartData: state });
    } catch (err) {
      console.error('[GiftOptions] add error:', err);
    } finally {
      if (!skipLoading) this.setLoading(false);
    }
  }

  async removeGiftItem(variantId, note = undefined) {
    this.setLoading(true);
    try {
      const payload = {
        updates:      { [variantId]: 0 },
        sections:     this.sectionsToRender(),
        sections_url: window.location.pathname,
      };
      if (note !== undefined) payload.note = note;
      const state = await this.cartUpdate(payload);
      this.cart?.renderContents(state);
      this.renderCartPageSections(state.sections);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-options', cartData: state });
    } catch (err) {
      console.error('[GiftOptions] remove error:', err);
    } finally {
      this.setLoading(false);
    }
  }

  async cartUpdate(payload) {
    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`Cart update failed: HTTP ${response.status}`);
    return response.json();
  }

  sectionsToRender() {
    const sections = this.cart?.getSectionsToRender().map((section) => section.id) ?? [];
    if (window.location.pathname.includes('/cart')) {
      const cartItemsId  = document.getElementById('main-cart-items')?.dataset.id;
      const cartFooterId = document.getElementById('main-cart-footer')?.dataset.id;
      if (cartItemsId)  sections.push(cartItemsId);
      if (cartFooterId) sections.push(cartFooterId);
    }
    return sections;
  }

  renderCartPageSections(sections) {
    ['main-cart-items', 'main-cart-footer'].forEach((elId) => {
      const wrapper = document.getElementById(elId);
      if (!wrapper) return;
      const sectionId = wrapper.dataset.id;
      if (!sectionId || !sections[sectionId]) return;
      const newContent = new DOMParser().parseFromString(sections[sectionId], 'text/html').querySelector('.js-contents')?.innerHTML;
      const target = wrapper.querySelector('.js-contents');
      if (newContent !== undefined && target) target.innerHTML = newContent;
    });
  }

  handleMessageInput(event) {
    const value = event.target.value;
    this.updateCounter(value.length);
    this.updateGiftItemMessages(value);

    this.pendingSaveNote = value;
    this.setMessageStatus('pending');

    clearTimeout(this.messageSaveTimer);
    this.messageSaveTimer = setTimeout(() => {
      this.messageSaveTimer = null;
      this.pendingSaveNote  = undefined;
      this.saveCartNote(value);
    }, 400);
  }

  /**
   * Flush any unsaved note immediately via sendBeacon on page unload.
   * sendBeacon survives page navigation; fetch() does not.
   */
  flushPendingNote() {
    const note = this.pendingSaveNote ?? this.inFlightNote;
    if (note === undefined) return;

    clearTimeout(this.messageSaveTimer);
    this.messageSaveTimer = null;
    this.pendingSaveNote  = undefined;
    this.inFlightNote     = undefined;

    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify({ note })], { type: 'application/json' });
      navigator.sendBeacon(routes.cart_update_url, blob);
    } else {
      this.saveCartNote(note);
    }
  }

  updateCounter(length) {
    const counter = this.querySelector('[data-gift-message-count]');
    if (counter) counter.textContent = length;
  }

  updateGiftItemMessages(text) {
    document.querySelectorAll('[id^="CartDrawer-Item-"], [id^="CartItem-"]').forEach((row) => {
      if (!row.classList.contains('cart-item--gift')) return;

      const details = row.querySelector('.cart-item__details');
      if (!details) return;

      let msgEl = details.querySelector('.cart-item__gift-message');

      if (text.trim() === '') {
        msgEl?.remove();
        return;
      }

      if (!msgEl) {
        const titleEl = details.querySelector('.cart-item__name');
        msgEl = document.createElement('p');
        msgEl.className = 'cart-item__gift-message';
        if (titleEl?.nextSibling) {
          details.insertBefore(msgEl, titleEl.nextSibling);
        } else {
          details.appendChild(msgEl);
        }
      }

      msgEl.textContent = text;
    });
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching cart data: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      return null;
    }
  }

  async updateLineItem(giftItem, lineno, note) {
    if (!giftItem || !lineno) return;
    const properties = { ...giftItem.properties, 'Gift Message': note };
    const changeUrl = routes.cart_change_url.endsWith('.js')
      ? routes.cart_change_url
      : routes.cart_change_url + '.js';
    await fetch(changeUrl, {
      ...fetchConfig(),
      body: JSON.stringify({
        line: lineno,
        quantity: giftItem.quantity,
        properties,
      }),
    }).then((data) => { console.log('res::', data); })
      .catch((err) => console.error('[GiftOptions] line-item property save error:', err));
  }

  async saveCartNote(note) {
    const cartData  = await this.getCartData();
    const giftIndex = cartData?.items.findIndex(p => p.properties._is_gift_option);
    const giftItem  = giftIndex !== -1 ? cartData.items[giftIndex] : null;
    const lineno    = giftIndex !== -1 ? giftIndex + 1 : null;

    const checkoutCTAs = [
      document.querySelector('#CartDrawer-Checkout'),
      document.querySelector('#CartDrawer-ViewBag'),
      document.querySelector('#checkout'),
    ].filter(Boolean);
    checkoutCTAs.forEach(el => { el.disabled = true; });

    this.inFlightNote = note;
    this.setMessageStatus('saving');
    try {
      await this.updateLineItem(giftItem, lineno, note);
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ note }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (this.inFlightNote === note) this.inFlightNote = undefined;
      this.setMessageStatus('saved');
    } catch (err) {
      console.error('[GiftOptions] note save error:', err);
      if (this.inFlightNote === note) this.inFlightNote = undefined;
      this.setMessageStatus('');
    } finally {
      checkoutCTAs.forEach(el => { el.disabled = false; });
    }
  }

  // States: '' | 'pending' | 'saving' | 'saved'
  setMessageStatus(state) {
    const el = this.querySelector('[data-gift-message-status]');
    if (!el) return;

    clearTimeout(this.statusClearTimer);
    el.dataset.status = state;

    if (state === 'pending' || state === 'saving') {
      el.textContent = 'Saving…';
    } else if (state === 'saved') {
      el.textContent = '✓ Saved';
      this.statusClearTimer = setTimeout(() => {
        el.dataset.status = '';
        el.textContent    = '';
      }, 2500);
    } else {
      el.textContent = '';
    }

    if (this.isCartPage) {
      const confirmBtn = this.querySelector('[data-gift-confirm]');
      if (confirmBtn) {
        const isBusy = state === 'pending' || state === 'saving';
        confirmBtn.disabled = isBusy;
        confirmBtn.classList.toggle('gift-options__confirm-btn--busy', isBusy);
      }
    }
  }

  refreshNoteFromServer() {
    fetch(routes.cart_url + '.js')
      .then((r) => r.json())
      .then((cart) => {
        const input = this.querySelector('[data-gift-message]');
        if (!input) return;
        const note = cart.note || '';
        input.value = note;
        this.updateCounter(note.length);
        this.updateGiftItemMessages(note);
      })
      .catch(() => {});
  }

  toggleCartPage() {
    cartPagePanelOpen = true;
    this.classList.add('gift-options--cart-open');
    document.querySelectorAll('[data-gift-options-edit]').forEach((b) => {
      b.setAttribute('aria-expanded', 'true');
    });
      requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
  }

  closeCartPage() {
    cartPagePanelOpen = false;
    this.classList.remove('gift-options--cart-open');
    document.querySelectorAll('[data-gift-options-edit]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  handleCollapseToggle() {
    this.panelCollapsed = !this.panelCollapsed;
    // Persist open/closed state so re-renders (cart updates) don't reset it.
    if (!this.isCartPage) cartDrawerPanelOpen = !this.panelCollapsed;
    this.panel?.classList.toggle('gift-options__panel--hidden', this.panelCollapsed);
    this.collapseBtn?.setAttribute('aria-expanded', String(!this.panelCollapsed));
    this.collapseBtn?.classList.toggle('gift-options__collapse-btn--up', this.panelCollapsed);
  }

  setUIActive(active) {
    this.dataset.active = active ? 'true' : 'false';
    this.toggleBtn?.setAttribute('aria-checked', String(active));
    this.toggleBtn?.classList.toggle('gift-options__toggle-btn--active', active);

    // Reset manual collapse state on every toggle change
    this.panelCollapsed = false;
    this.panel?.classList.toggle('gift-options__panel--hidden', !active);

    // Show collapse arrow only when gift is active; reset to down-arrow direction
    this.collapseBtn?.classList.toggle('gift-options__collapse-btn--hidden', !active);
    if (active) {
      this.collapseBtn?.setAttribute('aria-expanded', 'true');
      this.collapseBtn?.classList.remove('gift-options__collapse-btn--up');
    }

    const [noLabel, yesLabel] = this.querySelectorAll('.gift-options__switch-text');
    if (noLabel)  noLabel.classList.toggle('gift-options__switch-text--dim', active);
    if (yesLabel) yesLabel.classList.toggle('gift-options__switch-text--dim', !active);
  }

  updateOptionHighlight(selectedVariant) {
    this.radios.forEach((radio) => {
      const label = radio.closest('.gift-options__option');
      if (label) {
        label.classList.toggle('gift-options__option--selected', radio.value === selectedVariant);
      }
    });
  }

  setLoading(loading) {
    this.dataset.loading = loading ? 'true' : 'false';
    if (this.toggleBtn) this.toggleBtn.disabled = loading;
    this.radios.forEach((r) => { r.disabled = loading; });
  }
}

if (!customElements.get('gift-options')) {
  customElements.define('gift-options', GiftOptions);
}
